import { atom, useAtom } from "jotai";
import { atomWithStorage } from "jotai/utils";
import { useCallback } from "react";
import authAPI from "../services/auth";

export const AuthStatus = {
  UNKNOWN: 0,
  GUEST: 1,
  AUTHENTICATED: 2
};

const accountAtom = atomWithStorage('mea-account', undefined)
const schoolAtom = atomWithStorage('mea-last-school-id', undefined)
const membershipAtom = atom(undefined)

export function useAuthContext() {

  const [account, setAccount] = useAtom(accountAtom)
  const [lastSchoolId, setLastSchoolId] = useAtom(schoolAtom);
  const [currentMembership, setCurrentMembership] = useAtom(membershipAtom);

  let status = AuthStatus.UNKNOWN;
  switch (account) {
    case null:
      status = AuthStatus.GUEST;
      break;
    case undefined:
      status = AuthStatus.UNKNOWN;
      break;
    default:
      status = AuthStatus.AUTHENTICATED;
      break;
  }

  let checkAuthenticated = useCallback(async () => {
    try {
      const result = await authAPI.me();
      setAccount(result?.data)
      setLastSchoolId(result?.data?.preferences?.last_visited_school || null);
    } catch (error) {
      setAccount(null)
      setLastSchoolId(null);
      setCurrentMembership(undefined);
    }
  }, [setAccount, setLastSchoolId, setCurrentMembership])

  let login = useCallback(async (username, password, type, captcha) => {
    try {
      const result = await authAPI.login(username, password, type, captcha);
      if (result?.success === true) {
        setAccount(result?.data)
        setLastSchoolId(result?.data?.preferences?.last_visited_school || null);
        return true
      } else {
        setAccount(null)
        setLastSchoolId(null);
        setCurrentMembership(undefined);
        const err = new Error(result?.errorMessage || "Échec de connexion");
        err.captcha_required = result?.captcha_required;
        throw err;
      }
    } catch (error) {
      setAccount(null)
      setLastSchoolId(null);
      setCurrentMembership(undefined);
      throw error;
    }
  }, [setAccount, setLastSchoolId, setCurrentMembership])

  let loginWithCode = useCallback(async (username, code, captcha) => {
    try {
      const result = await authAPI.loginWithCode(username, code, captcha);
      if (result?.success === true) {
        setAccount(result?.data)
        setLastSchoolId(result?.data?.preferences?.last_visited_school || null);
        return true
      } else {
        setAccount(null)
        setLastSchoolId(null);
        setCurrentMembership(undefined);
        const err = new Error(result?.errorMessage || "Code d'authentification incorrect");
        throw err;
      }
    } catch (error) {
      setAccount(null)
      setLastSchoolId(null);
      setCurrentMembership(undefined);
      throw error;
    }
  }, [setAccount, setLastSchoolId, setCurrentMembership])

  let logout = useCallback(async () => {
    try {
      const result = await authAPI.logout();
      if (result?.success === true) {
        setAccount(null)
        setLastSchoolId(null);
        setCurrentMembership(undefined);
        return true
      } else {
        setAccount(null)
        setLastSchoolId(null);
        setCurrentMembership(undefined);
        return false
      }
    } catch (error) {
      setAccount(null)
      setLastSchoolId(null);
      setCurrentMembership(undefined);
      return false
    }
  }, [setAccount, setLastSchoolId, setCurrentMembership])

  const switchSchoolMembership = useCallback(async (membership) => {
    try {
      setLastSchoolId(membership?.school_id || null);
      setCurrentMembership(membership);
    } catch (error) {
      console.log(error);
      setCurrentMembership(undefined);
      setLastSchoolId(null);
    }
  }, [setLastSchoolId, setCurrentMembership]);

  const updateCurrentMembershipPreferences = useCallback((preferences) => {
    if (!account || !currentMembership) return;
    const updatedAccount = { ...account };
    updatedAccount.memberships = (account.memberships || []).map(m => {
      if (m.id === currentMembership.id) {
        const updatedMember = { ...m, preferences };
        setCurrentMembership(updatedMember);
        return updatedMember;
      }
      return m;
    });
    setAccount(updatedAccount);
  }, [account, currentMembership, setAccount, setCurrentMembership]);

  const updateAccountPreferences = useCallback((preferences) => {
    if (!account) return;
    const updatedAccount = { ...account, preferences };
    setAccount(updatedAccount);
  }, [account, setAccount]);

  const hasPermission = useCallback((permission) => {
    if (!permission) {
      return false;
    }
    if (!account) {
      return false;
    }
    if (!currentMembership) {
      return false;
    }
    return currentMembership?.permissions?.includes(permission);
  }, [account, currentMembership]);

  const auth = {
    access_token: ""
  }

  return {
    account,
    status,
    checkAuthenticated,
    login,
    loginWithCode,
    logout,
    lastSchoolId,
    currentMembership,
    switchSchoolMembership,
    updateCurrentMembershipPreferences,
    updateAccountPreferences,
    hasPermission,
    auth, //deprecated
  };
}